<script setup lang="ts">
import type { ItemMenu } from '../../menu/types';

import type { PartnerPortalContactSessionFragmentFragment } from '@/graphql/pp/generated/graphql';
import type { MenuTheme } from '@/ui/Menu/index';

type ItemMenuLink = Extract<ItemMenu, { to: string }>;

const props = defineProps<{
  partner: PartnerPortalContactSessionFragmentFragment,
  theme: MenuTheme,
}>();
const route = useRoute();
const {
  hasLiabilityWaiver,
  canViewLiabilityCategories,
  canViewLiabilityQuestions,
  canViewLiabilityContent,
  canViewLiabilityTemplates,
  canViewLiabilityForms,
  canViewLiabilitySubmissions,
} = usePartner();

const liabilityWaiverBase = computed(() => `/${props.partner.partner.uuid}/liability-waiver`);
const liabilityWaiverSubmenu = computed<ItemMenuLink[]>(() => {
  const items: ItemMenuLink[] = [];
  if (canViewLiabilityCategories.value) {
    items.push({
      faIcon: ['fal', 'layer-group'],
      title: 'Categories',
      to: `${liabilityWaiverBase.value}/categories`,
      key: `${liabilityWaiverBase.value}/categories`,
    });
  }
  if (canViewLiabilityQuestions.value) {
    items.push({
      faIcon: ['fal', 'circle-question'],
      title: 'Questions',
      to: `${liabilityWaiverBase.value}/questions`,
      key: `${liabilityWaiverBase.value}/questions`,
    });
  }
  if (canViewLiabilityContent.value) {
    items.push({
      faIcon: ['fal', 'book-open'],
      title: 'Content',
      to: `${liabilityWaiverBase.value}/content`,
      key: `${liabilityWaiverBase.value}/content`,
    });
  }
  if (canViewLiabilityTemplates.value) {
    items.push({
      faIcon: ['fal', 'table-layout'],
      title: 'Templates',
      to: `${liabilityWaiverBase.value}/templates`,
      key: `${liabilityWaiverBase.value}/templates`,
    });
  }
  if (canViewLiabilityForms.value) {
    items.push({
      faIcon: ['fal', 'file-lines'],
      title: 'Forms',
      to: `${liabilityWaiverBase.value}/forms`,
      key: `${liabilityWaiverBase.value}/forms`,
    });
  }
  if (canViewLiabilitySubmissions.value) {
    items.push({
      faIcon: ['fal', 'paper-plane'],
      title: 'Submissions',
      to: `${liabilityWaiverBase.value}/submissions`,
      key: `${liabilityWaiverBase.value}/submissions`,
    });
  }
  return items;
});

const menuItems = computed<ItemMenu[]>(() => {
  if (!hasLiabilityWaiver.value || !liabilityWaiverSubmenu.value.length) {
    return [];
  }
  return [
    {
      faIcon: ['fal', 'file-contract'],
      title: 'Liability Waiver',
      key: 'liability-waiver',
      submenu: liabilityWaiverSubmenu.value,
    },
  ];
});

const liabilityWaiverRelatedPaths = computed<Record<string, string[]>>(() => ({
  [`${liabilityWaiverBase.value}/categories`]: [`${liabilityWaiverBase.value}/users`],
  [`${liabilityWaiverBase.value}/questions`]: [`${liabilityWaiverBase.value}/lists`],
}));

const selectedKey = computed(() => {
  const matchesPath = (path: string) => route.path === path || route.path.startsWith(`${path}/`);
  const activeItem = liabilityWaiverSubmenu.value.find((item) => (
    [item.key, ...(liabilityWaiverRelatedPaths.value[item.key] ?? [])].some(matchesPath)
  ));
  return activeItem?.key ?? route.path;
});
</script>

<template>
  <template v-if="menuItems.length">
    <LayoutSidebarMenuTitle
      title="CampusCoverage"
      class="mb-4 pl-4 text-(--color-sidebar-text)"
    />
    <Menu
      :items="menuItems"
      :selected="selectedKey"
      class="mb-5"
      :theme="theme"
      data-cy="menu-liability-waiver"
    />
  </template>
</template>
